import type {
  AppUser,
  CreateOrganization,
  Organization,
  Page,
  UpdateOrganization,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const base = adminPath('/organizations');

/** `GET /admin/v1/organizations` */
export function useOrganizations(options: { pageSize?: number; enabled?: boolean } = {}) {
  const { request, keys } = useApiContext();
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  return usePagedList<Organization>({
    queryKey: keys.organizations.list({ pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<Organization>>('GET', base, { query: pageQuery(cursor, pageSize), signal }),
    enabled: options.enabled,
  });
}

export function useOrganization(organizationId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.organizations.detail(organizationId ?? ''),
    queryFn: ({ signal }) => request<Organization>('GET', `${base}/${organizationId}`, { signal }),
    enabled: organizationId !== undefined,
  });
}

/** `GET /admin/v1/organizations/{id}/members` */
export function useOrganizationMembers(
  organizationId: string | undefined,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  const { request, keys } = useApiContext();
  return usePagedList<AppUser>({
    queryKey: [...keys.organizations.members(organizationId ?? ''), { pageSize }],
    fetchPage: (cursor, signal) =>
      request<Page<AppUser>>('GET', `${base}/${organizationId}/members`, {
        query: pageQuery(cursor, pageSize),
        signal,
      }),
    enabled: organizationId !== undefined,
  });
}

export function useCreateOrganization() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateOrganization) => request<Organization>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.organizations.all),
  });
}

export function useUpdateOrganization() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateOrganization }) =>
      request<Organization>('PATCH', `${base}/${id}`, { body: patch }),
    onSuccess: (_org, { id }) => invalidate(keys.organizations.all, keys.organizations.detail(id)),
  });
}

export function useDeleteOrganization() {
  const { request, keys, invalidate, remove } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<void>('DELETE', `${base}/${id}`),
    onSuccess: (_void, id) => {
      remove(keys.organizations.detail(id), keys.organizations.members(id));
      return invalidate(keys.organizations.all);
    },
  });
}

export function useAddOrganizationMember() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ organizationId, userId }: { organizationId: string; userId: string }) =>
      request<unknown>('PUT', `${base}/${organizationId}/members/${userId}`, { body: {} }),
    onSuccess: (_r, { organizationId }) =>
      invalidate(
        keys.organizations.members(organizationId),
        keys.organizations.detail(organizationId),
        keys.organizations.all,
      ),
  });
}

export function useRemoveOrganizationMember() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ organizationId, userId }: { organizationId: string; userId: string }) =>
      request<void>('DELETE', `${base}/${organizationId}/members/${userId}`),
    onSuccess: (_r, { organizationId }) =>
      invalidate(
        keys.organizations.members(organizationId),
        keys.organizations.detail(organizationId),
        keys.organizations.all,
      ),
  });
}
